/**
 * MovementTwinPage — iter629 — Двигателен близнак (14-ти).
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Loader2, ArrowLeft, Footprints, Plus } from "lucide-react";
import HeartRateZonesCard from "@/components/cardio/HeartRateZonesCard";
import ExerciseBpCard from "@/components/cardio/ExerciseBpCard";
import { toast } from "sonner";
import { twinLabel } from "@/lib/twinCatalog";

import { MobileTopBar } from "@/components/MobileTopBar";
export default function MovementTwinPage() {
  const { API } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [snap, setSnap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    steps: "", active_minutes_moderate: "", active_minutes_vigorous: "",
    sedentary_hours: "", vo2max_estimate: "",
  });
  const [saving, setSaving] = useState(false);
  const [strength, setStrength] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/movement-twin/snapshot`);
      setSnap(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [API]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== "").map(([k, v]) => [k, parseFloat(v)])
      );
      payload.strength_session = strength;
      if (Object.keys(payload).length === 1) { toast.error("Попълни поне едно поле."); return; }
      await axios.post(`${API}/movement-twin/log`, payload);
      window.dispatchEvent(new Event("movement-log-changed"));
      window.dispatchEvent(new Event("twin-score-changed"));
      toast.success("Записано!");
      setForm({ steps: "", active_minutes_moderate: "", active_minutes_vigorous: "", sedentary_hours: "", vo2max_estimate: "" });
      setStrength(false);
      load();
    } catch {
      toast.error("Грешка при запис.");
    } finally { setSaving(false); }
  };

  const cardCls = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`} data-testid="movement-twin-page">
      <MobileTopBar title="Движение" fallbackTo="/dashboard" testid="movement-twin-mobile-topbar" />
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <button onClick={() => navigate("/dashboard")} className="text-xs opacity-60 hover:opacity-100 mb-3 flex items-center gap-1"><ArrowLeft className="w-3.5 h-3.5" /> Към таблото</button>
        <div className="flex items-center gap-2 mb-3">
          <Footprints className="w-5 h-5 text-cyan-500" />
          <h1 className="text-xl font-bold">{twinLabel("movement")}</h1>
        </div>
        <p className={`text-xs mb-4 ${isDark ? "text-slate-400" : "text-slate-600"}`}>Следи стъпки, активни минути и заседнало време. Цел: 10к стъпки + 150 мин/седмично умерена активност (WHO).</p>

        {loading ? <Loader2 className="w-6 h-6 animate-spin text-cyan-500 mx-auto my-12" /> : (
          <>
            <div className={`p-5 rounded-2xl border-2 mb-4 ${cardCls}`} data-testid="movement-snapshot">
              <div className="flex items-baseline gap-3 mb-2">
                <span className="text-4xl font-bold" data-testid="movement-score">{snap?.composite ?? "—"}</span>
                <span className="text-xs opacity-60">/100</span>
              </div>
              <p className="text-sm mt-2" data-testid="movement-interpretation">{snap?.interpretation_bg}</p>
              {snap?.avg_steps_7d != null && (
                <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                  <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-slate-100"}`}><div className="opacity-60 text-[10px]">Avg стъпки/ден (7д)</div><div className="font-bold">{snap.avg_steps_7d}</div></div>
                  <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-slate-100"}`}><div className="opacity-60 text-[10px]">Активни мин (7д)</div><div className="font-bold">{snap.weekly_active_min} мин</div></div>
                  <div className={`p-2 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-slate-100"}`}><div className="opacity-60 text-[10px]">VO2 max</div><div className="font-bold">{snap.latest_vo2 ?? "—"}</div></div>
                </div>
              )}
            </div>

            <div className={`p-5 rounded-2xl border mb-4 ${cardCls}`} data-testid="movement-log-form">
              <h2 className="font-bold text-sm mb-3 flex items-center gap-1"><Plus className="w-4 h-4" /> Лог за днес</h2>
              <div className="grid grid-cols-2 gap-3 mb-3">
                {[
                  { k: "steps", label: "Стъпки", ph: "8500" },
                  { k: "active_minutes_moderate", label: "Умерени минути", ph: "30" },
                  { k: "active_minutes_vigorous", label: "Интензивни минути", ph: "15" },
                  { k: "sedentary_hours", label: "Седнало време (ч)", ph: "8" },
                  { k: "vo2max_estimate", label: "VO2 max (опц.)", ph: "42" },
                ].map(f => (
                  <label key={f.k} className="flex flex-col gap-1">
                    <span className="text-[10px] opacity-70">{f.label}</span>
                    <input type="number" step="0.1" placeholder={f.ph} value={form[f.k]} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} data-testid={`movement-input-${f.k}`} className={`px-2 py-1.5 rounded-md border text-sm ${isDark ? "bg-slate-900 border-slate-700" : "bg-white border-slate-300"}`} />
                  </label>
                ))}
              </div>
              <label className="flex items-center gap-2 mb-3 text-sm">
                <input type="checkbox" checked={strength} onChange={(e) => setStrength(e.target.checked)} className="accent-cyan-500" data-testid="movement-strength-toggle" />
                Силова сесия днес
              </label>
              <Button onClick={save} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 text-white w-full" data-testid="movement-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />} Запиши
              </Button>
            </div>

            {/* iter1057 — зоните идват от кардио близнака (пулсов резерв) */}
            <HeartRateZonesCard isDark={isDark} />

            {/* iter1058 — как реагира налягането при сила срещу аеробно */}
            <div className="mt-4"><ExerciseBpCard isDark={isDark} /></div>
          </>
        )}
      </div>
    </div>
  );
}
